import {useNavigationConfig} from '@rdcomponents/app/routing/hooks/use-app-navigation';
import {NavigationItemConfigType} from '@rdcomponents/app/routing/routing-type';
import {FAVORITES_SECTION_KEY} from '@rdcomponents/navigation/favorite-routes/favorites-helper';
import {MenuItemWithFavoriteStar} from '@rdcomponents/navigation/menu-item-with-favorite/menu-item-with-favorite';
import {BetaSup} from '@rdcomponents/sup/beta-sup';
import {NewSup} from '@rdcomponents/sup/new-sup';
import {Text} from '@rdcomponents/text/text';
import {useContext, useMemo} from 'react';

import {appRoute} from '../../../../app-route';
import {NavigationLink} from '../../../../layout/navigation-link/navigation-link';
import {useSystem} from '../../../../provider/system/system-hook';
import {UiContext} from '../../../../provider/ui/ui-context';
import {UiContextType} from '../../../../provider/ui/ui-context-type';
import {useUser} from '../../../../provider/user/user-hook';

import * as styles from './menu-item-renderer.scss';

type PropsType = {
    navigationConfigItem: NavigationItemConfigType;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function MenuItemRenderer({navigationConfigItem}: PropsType): JSX.Element {
    const {
        label,
        labelComponent,
        showBeta,
        showNew,
        children,
        allowedSearchParameters,
        path,
        key,
        isOnlyRoute: itemIsOnlyRoute,
        isCustom: isCustomItem,
    } = navigationConfigItem;

    const childPaths = useMemo(() => {
        return (
            children?.flatMap((item) => {
                if (item.path) {
                    return [item.path];
                }

                return [];
            }) || []
        );
    }, [children]);

    const {isLeftSideOpen} = useContext<UiContextType>(UiContext);
    const {screen} = useSystem();

    const {menuItems} = useNavigationConfig();
    const {favorites} = useUser();

    const showFavoriteStar = useMemo(() => {
        const isInlineCollapsed = screen.isDesktop && !isLeftSideOpen;

        if (!isInlineCollapsed && path && !itemIsOnlyRoute && !isCustomItem && key !== FAVORITES_SECTION_KEY) {
            return favorites?.includes(path)
                ? menuItems.favouriteMenuItems?.some((topLevelFavouriteItem) => {
                      return topLevelFavouriteItem.path === path;
                  })
                : !menuItems.favouriteMenuItems?.some((topLevelFavouriteItem) => {
                      return topLevelFavouriteItem.children?.some(
                          (favouriteNestedItem) => favouriteNestedItem.path === path
                      );
                  });
        }

        return false;
    }, [
        favorites,
        isCustomItem,
        isLeftSideOpen,
        itemIsOnlyRoute,
        key,
        menuItems?.favouriteMenuItems,
        path,
        screen.isDesktop,
    ]);

    const fullLabel =
        labelComponent ?? label ? (
            <span className={styles.MenuItemRenderer_labelRow}>
                <Text className={styles.MenuItemRenderer_labelRowText} ellipsis>
                    {labelComponent ?? label}
                </Text>
                {showBeta && <BetaSup />}
                {showNew && <NewSup />}
            </span>
        ) : (
            <div />
        );

    const linkLabel = children ? (
        fullLabel
    ) : (
        <NavigationLink allowedSearchParameters={allowedSearchParameters} to={path || appRoute.root.path}>
            {fullLabel}
        </NavigationLink>
    );

    return showFavoriteStar && path ? (
        <MenuItemWithFavoriteStar childPaths={childPaths} label={linkLabel} path={path} />
    ) : (
        linkLabel
    );
}
